import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { PawPrint, AlertTriangle, MapPin, HeartHandshake } from "lucide-react";

export default function TagPublic() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ message: "", contact: "", location: "" });
  const [sending, setSending] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [obsForm, setObsForm] = useState({ message: "", contact: "" });
  const [obsSending, setObsSending] = useState(false);

  useEffect(() => {
    api.get(`/tags/${code}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [code]);

  const send = async () => {
    if (!form.message) return toast.error("Descreva onde/como encontrou o pet");
    setSending(true);
    try {
      await api.post(`/tags/${code}/found`, form);
      toast.success("Tutor avisado! Obrigado por ajudar.");
      setOpen(false);
    } catch {
      toast.error("Erro ao avisar o tutor");
    } finally {
      setSending(false);
    }
  };

  const sendObs = async () => {
    if (!obsForm.message) return toast.error("Escreva a observação");
    setObsSending(true);
    try {
      await api.post(`/tags/${code}/observation`, obsForm);
      toast.success("Observação enviada ao tutor. Obrigado!");
      setObsOpen(false);
      setObsForm({ message: "", contact: "" });
    } catch {
      toast.error("Erro ao enviar");
    } finally {
      setObsSending(false);
    }
  };

  if (error) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Tag não encontrada.</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Carregando...</div>;

  const lost = data.pet?.lost?.active;

  return (
    <div className="max-w-lg mx-auto px-6 py-12" data-testid="tag-public-page">
      {lost && (
        <div className="bg-red-600 text-white rounded-2xl p-5 mb-6 text-center animate-pulse" data-testid="lost-banner">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p className="font-heading font-bold text-xl">ESTE PET ESTÁ DESAPARECIDO</p>
          {data.pet.lost.message && <p className="text-white/85 text-sm mt-2">{data.pet.lost.message}</p>}
          {data.pet.lost.location && <p className="text-white/70 text-xs mt-1 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> Último local: {data.pet.lost.location}</p>}
        </div>
      )}

      <div className="bg-white rounded-3xl overflow-hidden border shadow-sm">
        {data.pet?.photo ? (
          <img src={photoUrl(data.pet.photo)} alt={data.pet.name} className="w-full h-64 object-cover" />
        ) : (
          <div className="w-full h-48 bg-brand-mintLight flex items-center justify-center"><PawPrint className="w-14 h-14 text-brand-mint" /></div>
        )}
        <div className="p-7 text-center">
          <p className="text-xs font-mono text-muted-foreground mb-1">{data.tag_code}</p>
          <h1 className="font-heading font-extrabold text-3xl text-brand-graphite">
            {data.pet ? `Oi, eu sou ${data.pet.name}!` : "Tag PetMachine Hub"}
          </h1>
          {data.pet ? (
            <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
              <p>{data.pet.species}{data.pet.breed ? ` • ${data.pet.breed}` : ""}</p>
              {data.pet.allergies && <p className="text-red-600 font-medium">Alergias: {data.pet.allergies}</p>}
              <p className="pt-2">Meu tutor(a): <strong className="text-brand-graphite">{data.tutor_name}</strong> — {data.city}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-3">Esta tag ainda não foi vinculada a um pet.</p>
          )}

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className={`w-full rounded-full h-12 mt-7 font-semibold ${lost ? "bg-red-600 hover:bg-red-700" : "bg-brand-coral hover:bg-brand-coralDark"} text-white`} data-testid="found-pet-btn">
                <HeartHandshake className="w-5 h-5 mr-2" /> {lost ? "Encontrei este pet!" : "Avisar o tutor"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Avisar o tutor</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Textarea placeholder="Ex: Encontrei ele na Rua XV, perto da praça. Está seguro comigo!" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="found-message-input" />
                <Input placeholder="Onde encontrou? (local/bairro)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="found-location-input" />
                <Input placeholder="Seu contato (telefone/WhatsApp)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="found-contact-input" />
                <Button onClick={send} disabled={sending} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="found-submit-btn">
                  {sending ? "Enviando..." : "Enviar alerta ao tutor"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={obsOpen} onOpenChange={setObsOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" className="w-full mt-2 text-muted-foreground" data-testid="observation-btn">Deixar observação sobre este pet</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Observação para o tutor</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Textarea placeholder="Ex: este cachorro é arisco e pode morder / vive fugindo por esta rua / parecia machucado..." value={obsForm.message} onChange={(e) => setObsForm({ ...obsForm, message: e.target.value })} data-testid="observation-message" />
                <Input placeholder="Seu contato (opcional)" value={obsForm.contact} onChange={(e) => setObsForm({ ...obsForm, contact: e.target.value })} data-testid="observation-contact" />
                <Button onClick={sendObs} disabled={obsSending} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="observation-submit">
                  {obsSending ? "Enviando..." : "Enviar observação"}
                </Button>
                <p className="text-xs text-center text-muted-foreground">A observação é enviada somente ao tutor, não fica pública.</p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-6">Tag inteligente PetMachine Hub — petmachinehub.com.br</p>
    </div>
  );
}
